import { WidenRecordDeep } from '../../../src/types/widen';

type LocaleResourceBase = {
  [x: string]: string | LocaleResourceBase;
};

export const en = {
  main: {
    navbar: {
      home: 'Home',
      customers: 'Customers',
      about: 'Company',
      developers: 'Developers',
      language: 'Language',
    },
    hero: {
      topBadge: 'Global payments solutions',
      header: 'Send money anywhere',
      subHeading:
        'From dollars and euros to pesos and beyond, Sphere makes moving between currencies effortless.',
      ctas: {
        primary: 'For businesses',
        secondary: 'For individuals',
      },
    },
    solutions: {
      header: {
        topBadge: 'Optimized payment processing',
        header: 'Cross-border payments for businesses',
        subHeading:
          'Better money movement—from wire, bank transfer, and card to support for every major network—\ntransferring money over the internet has never been easier.',
        ctas: {
          primary: 'Get started',
          secondary: 'Learn more',
        },
        cards: {
          '0': {
            title: 'For international remittances',
            description:
              'Send your preferred currency, your vendors receive USD, MXN, BRL, EUR at world-class exchange rates.',
            bullets: {
              '0': 'Compliant & secure',
              '1': 'Near-instant settlement',
              '2': 'Global coverage',
            },
          },
          '1': {
            title: 'For high-net-worth individuals',
            description:
              'OTC services, effortless currency conversions, and comprehensive financial tracking and management solutions.',
            bullets: {
              '0': 'Dedicated support 24/7',
              '1': 'Deep liquidity ',
              '2': 'Privacy and encryption',
            },
          },
          '2': {
            title: 'For Finance & FinTechs',
            description:
              'Accept payments any way you want, leverage auto-conversions that settle directly to your bank, and offer conversion services to your customers with white-label solutions.',
            bullets: {
              '0': 'Service level agreements',
              '1': 'Customer guardrails',
              '2': 'API and No-Code Solutions',
            },
          },
        },
      },
    },
    ramp: {
      topBadge: 'Conversion solutions',
      header: 'Connecting the world’s currencies',
      subHeading:
        "Sphere handles the engineering, operational, and regulatory complexities for you — orchestrating so you don't have to.",
      solutions: {
        '0': {
          title: 'White-labeled solutions for better money movement',
          description:
            "Sphere handles the engineering, operational, and regulatory complexities for you — orchestrating so you don't have to.",
          bullets: {
            '0': 'Available by API, no-code, and embeddable widget.',
            '1': 'Save hundreds of hours on development time.',
            '2': 'Unrivaled pricing.',
          },
        },
        '1': {
          title: 'Currencies conversions made easy',
          description:
            'Generate a wallet tied to your bank account. Send currencies and receive USD with just one click. Available to select merchants through the Sphere dashboard.',
        },
        '2': {
          title: 'All-in-one compliance',
          description:
            'Rely on our global expertise and regulatory licenses to provide your users with the optimal payout experience. Allow us to handle transaction monitoring, sanction screening, and fraud detection on your behalf.',
        },
      },
    },
    dashboard: {
      topBadge: 'Features',
      header: 'Sphere dashboard',
      subHeading:
        'Sphere payment links are versatile, customizable and robust. They adapt to your needs, whether for one-time purchases or recurring subscriptions.',
      solutions: {
        '0': {
          title: 'Payment links',
          description:
            'Accept payments directly from email, SMS, social media, website, or app. Get the full power of Sphere without having to write a single line of code.',
        },
        '1': {
          title: 'Subscriptions',
          description:
            'Offer traditional or true on-chain subscriptions — directly from a card, bank account, or digital wallet. Enjoy flexible billing options, ranging from per-minute to annual, metered to per-seat, and volume to graduated tiers. Receive payments directly, without escrow, token unwrapping, gas relayers, or other unnecessary steps.',
        },
        '2': {
          title: 'Developer toolkit',
          description:
            'Sphere simplifies your engineering process with integrated payment features. We navigate global economic intricacies, allowing your teams to focus on seamless development on a single platform.',
        },
        '3': {
          title: 'Customer Insights and Analytics',
          description:
            'Track 15+ KPIs, from MRR and churn, to conversion and click-through rates.  Maximize earnings potential, increase authorization rates, get faster settlement, and reduce chargebacks by accepting more types of money. ',
        },
      },
    },
    developerTools: {
      topBadge: 'SDK',
      header: 'Developer toolkit',
      subHeading:
        'We follow the best practices in payments to deliver semantic, easy-to-use SDKs. Focus on growing your business while we handle smart contract audits, cross-chain orchestration, and all the technical, operational, and compliance upkeep.',
      bullets: {
        '0': 'Simple, powerful, and intuitive RESTful API',
        '1': 'Copy and paste code that just works',
        '2': 'Concise and thorough documentation',
      },
      ctas: {
        primary: 'Documentation',
        secondary: 'Developer support',
      },
    },
    features: {
      header: {
        topBadge: 'Product Features',
        title: 'An end-to-end payments suite',
        description:
          'Connect seamlessly through a single API or a user-friendly no-code console in just minutes.\nGain access to top-tier compliance, accounting, payments, on/off-ramps, analytics, and developer tools.',
      },
      grid: {
        '0': 'Comprehensive analytics',
        '1': 'Multiparty payments and payouts',
        '2': 'Build together with role-based ACLs',
        '3': 'AML & fraud detection',
        '4': 'Secured and certified',
        '5': 'Email notifications',
      },
      securityMarquees: {
        '0': 'OFAC\nsanctions',
        '1': 'FTC best practices',
        '2': 'GDPR best practices',
        '3': 'PCI\ncompliant',
      },
      benefits: {
        lowestFees: {
          title: 'Lowest Fees on the Market',
          description:
            'All transactions at a competitor matched fee, with network fees passed at cost.',
        },
        convenientSwaps: {
          title: 'Convenient Swaps',
          description:
            'Payouts directly to your or a third-party vendor’s bank account, either manually or on a scheduled basis, with competitive OTC rates.',
        },
        instantSettlements: {
          title: 'Instant Settlements',
          description:
            'Receive your money in seconds to minutes, instead of days to weeks.',
        },
        splitPayouts: {
          title: 'Split Payouts',
          description: 'Automatically split payments without extra fees.',
        },
        nonCustodial: {
          title: 'Non-Custodial',
          description:
            'Payments are peer to peer and we never handle customer or merchant funds.',
        },
        audited: {
          title: 'Audited',
          description:
            'Our contracts are audited by industry-leading experts. We regularly conduct threat modeling, penetration testing, and vulnerability assessments on our infrastructure.',
        },
        compliant: {
          title: 'Compliant',
          description:
            'Sphere follows all best practices according to US regulations and uses licensed money transmitters.',
        },
        encrypted: {
          title: 'Encrypted',
          description:
            'All data is encrypted and/or tokenized at rest and in transit. We never store sensitive PIIs, and have strict ACLs.',
        },
      },
    },
    ctaSection: {
      header: 'Ready to take the next step?',
      description: 'Join industry leaders growing with Sphere.',
      button: 'Get started',
    },
    faq: {
      header: 'FAQ',
      description:
        'Commonly asked questions. Can’t find the answer you’re looking for? Please get in touch.',
      questions: {
        '0': {
          q: 'How much does Sphere cost?',
          a: 'Software access is free, but we take a small transaction fee. Custom enterprise pricing is available upon request.',
        },
        '1': {
          q: 'Which networks are supported?',
          a: 'We support all major card networks (Visa, Mastercard, American Express, Discover) in 120+ countries, bank transfers, and domestic and international wires.',
        },
        '2': {
          q: 'How is currency conversion handled?',
          a: 'Customers consent to swapping to a different currency during transfer, which is routed through an exchange or subject to foreign exchange rates at cost.',
        },
        '3': {
          q: 'Is Sphere audited?',
          a: 'Yes, we are audited by OtterSec and PCI-DSS compliant. We volunteer for MSB/VASP requirements, and employ a robust KYC/AML program. We are in the process of receiving our SOC2 and ISO/IEC 27001 certifications.',
        },
        '4': {
          q: 'Does Sphere custody funds?',
          a: 'Never. We are a technology services provider, not a MSB/VASP nor money transmitter. All payments that involve custody are handled by licensed money transmitters, qualified custodians, and chartered banks.',
        },
        '5': {
          q: 'Is it possible to integrate Sphere with my existing payment solution?',
          a: 'Yes. You can use your own Stripe or other payment providers, and use Sphere to add support for alternative payments and back-office automation through our API/SDK. We designed our API to use similar data flows and object models to reduce the amount of infrastructure to rewrite.',
        },
      },
    },
    footer: {
      slogan:
        'From dollars and euros to pesos and beyond, Sphere makes moving between currencies effortless.',
      termsOfService: 'Terms of Service & Privacy Policy',
      privacyPolicy: 'Privacy Policy',
      reportVulnerability: 'Report a Vulnerability',
    },
  },
  accessRequest: {
    steps: {
      '1': {
        title: 'Request Access',
        descriptionParagraph1:
          'For <0>individuals</0> interested in using our on/off ramp functionality, you can now do so without needing to contact us. <0>Simply visit</0> <1>spherepay.co/ramp</1> to sign up and get started.',
        descriptionParagraph2:
          'For businesses or other inquiries, please fill out the information below and a Sphere expert will contact you shortly.',
        form: {
          walletAddress: 'Wallet Address',
          firstName: 'First Name',
          lastName: 'Last Name',
          email: 'Work Email',
          businessName: 'Business Name',
          businessUrl: 'Business Website URL',
          phoneNumber: 'Phone Number',
        },
        notYourEmail: 'Not your email?',
        errors: {
          emailAlreadyInUse:
            'This email is already in use for an active request. Please try another.',
          emailAlreadyLinkedToAccount:
            'This email is already linked to an account. Login instead?',
        },
      },
      '2': {
        title: 'Contact Information',
        form: {
          preferredContactMethod: 'Preferred Contact Method',
          preferredContactMethodDescription:
            'Account access details will be sent via email.',
          placeholders: {
            telegram: '@username',
            whatsapp: '+1234567890',
            email: 'you@example.com',
            sms: '+1234567890',
          },
        },
      },
      '3': {
        title: 'Business Details',
        form: {
          bankAccountJurisdiction:
            'Does your business have a bank account in any of the following jurisdictions?',
          bankAccountJurisdictionsOptions: {
            unitedStates: 'United States (USD)',
            europeanUnion: 'European Union (EUR)',
            noneOfTheAbove: 'None of the above',
          },
          preferredCurrencies:
            'Which currencies would you like to use for on- and off-ramp transactions?',
          preferredCurrenciesDescription: 'Please select at least one.',
          otherCurrencyPlaceholder: 'Please specify',
        },
      },
      '4': {
        title: 'Business Details',
        submitApplication: 'Submit application',
        form: {
          businessOperatingAddress: 'Business Operating Address',
          businessCountry: 'In which country is your business located?',
          countryPlaceholder: 'Country',
          statePlaceholder: 'State',
          monthlyTransferVolume: 'Monthly Transfer Volume',
          estimatedTransactionVolume:
            'What was your estimated transaction volume last month?',
          selectPlaceholder: 'Please select',
          monthlyVolume: {
            '0_4999': 'Less than $4,999',
            '5000_9999': '$5,000 - $9,999',
            '10000_99999': '$10,000 - $99,999',
            '100000_499999': '$100,000 - $499,999',
            '500000_999999': '$500,000 - $999,999',
            '1000000_5000000': '$1,000,000 - $5,000,000',
            '5000000_plus': 'More than $5,000,000',
          },
          additionalDetails: 'Additional Details',
          additionalDetailsDescription:
            'Describe the nature of your business and your goals for the account.',
        },
      },
    },
    submitted: {
      title: 'Your sign up has been\nsuccessfully submitted',
      defaultBody:
        "Thanks for indicating your interest. Our team will review your information and be in touch <0>via email</0> if we're able to extend you access to an account.",
      bridgeBody:
        'Thanks for indicating your interest. Our team will review your information and be in touch <0>via email</0> when we might be able to serve you.',
      conciergeBody:
        "Great! Someone from our team will be in touch shortly. If there's any delay, please feel free to reach out to us directly here:",
      returnToWebsite: 'Return to website',
      contactUs: 'Connect with your onboarding specialist',
    },
    googleSplash: {
      pendingTitle: 'Your account is still\npending approval',
      noAccountTitle: 'This Gmail is not associated\nwith a Sphere account',
      applicationId: 'Your Application ID:',
      pendingDescription:
        'We have received your Sphere application and are processing your request. We will contact you soon with an update on your account status as soon as possible.',
      noAccountDescription:
        "This Gmail isn't linked to a Sphere account.\nComplete an application to get started.",
      returnToLogin: 'Return to login',
      fillOutApplication: 'Fill out application',
    },
    loadingAnimation: {
      calculatingRates: 'Calculating the best exchange rates...',
      securingTransactions: 'Securing your transactions...',
      optimizingPayments: 'Optimizing global payments...',
      connectingCurrencies: 'Connecting currencies...',
      ensuringCompliance: 'Ensuring compliance standards...',
      processingData: 'Processing data securely...',
      almostThere: 'Almost there...',
    },
    passwordSetup: {
      loginSuccess: 'Login success! Welcome back {{email}}',
      newLinkSent: 'A new link has been sent to your email.',
      regenerateError:
        'An error occurred while regenerating the access token. Please try again.',
      tokenNotFoundOrInvalid: 'Token not found or invalid',
      tokenNotFoundOrInvalidDescription:
        "The access token you're trying to use is either not found or invalid. This might be due to expiration or an error in the link.",
      returnToWebsite: 'Return to website',
      contactSupport: 'Contact support',
      linkExpired: 'Your link has expired',
      linkExpiredDescription:
        'The email link has expired. Please generate a new one to complete your sign-up.',
      redirecting_zero: 'Redirecting you now...',
      redirecting_one: 'Redirecting you in 1 second...',
      redirecting_other: 'Redirecting you in {{count}} seconds...',
      regenerateLink: 'Regenerate link',
      createSignIn: 'Create Sign In',
      createAccount: 'Create account',
      email: 'Email',
      password: 'Password',
      enterPassword: 'Enter Password',
      confirmPassword: 'Confirm Password',
    },
  },
  validation: {
    required: 'Required',
    invalidEmail: 'Please enter a valid email',
    emailTooLong: 'Email must be less than 255 characters',
    firstNameTooLong: 'First name must be less than 50 characters',
    lastNameTooLong: 'Last name must be less than 50 characters',
    businessNameTooLong: 'Business name must be less than 50 characters',
    businessUrlTooLong: 'Business website URL must be less than 255 characters',
    phoneNumberTooLong: 'Phone number must be less than 20 characters',
    phoneNumberInvalid:
      'Please enter a valid phone number in international format (e.g., +1234567890)',
    preferredContactMethodValueTooLong:
      'Preferred contact method value must be less than 255 characters',
    weakPassword:
      'Password must be at least 6 characters long and contain at least one letter and one number',
    passwordsDoNotMatch: 'Passwords do not match',
  },
  general: {
    comingSoon: 'Coming soon',
    support: 'Support',
    optional: 'Optional',
    back: 'Back',
    next: 'Next',
    continue: 'Continue',
    yes: 'Yes',
    no: 'No',
    other: 'Other',
    selectAllThatApply: 'Select all that apply',
    unknownError: 'An unknown error occurred. Please try again later.',
  },
  // ramp-widget translations
  translations: {
    validation: {
      required: 'Required',
      email: 'Invalid email',
      passwordsMatch: 'Passwords do not match',
      invalidCountry: 'Invalid country',
      invalidState: 'Invalid state',
      invalidIban: 'Invalid IBAN',
      invalidWalletAddress: 'Invalid wallet address',
      positive: 'Enter a positive number',
      maxLength: {
        wireMessage: 'Wire message must be less than 105 characters',
      },
      invalidSortCode: 'Sort code must be in the format XX-XX-XX or XXXXXX',
      invalidAccountNumber: 'Invalid account number',
      phoneNumberInvalid:
        'Please enter a valid phone number in international format (e.g., +1234567890)',
    },
    sidebar: {
      createPayout: 'Create transfer',
      withdrawToBank: 'Withdraw to bank',
      depositToWallet: 'Deposit to wallet',
      myAccounts: 'My accounts',
      accounts: 'Accounts',
      payoutHistory: 'Transfer history',
      history: 'History',
      settings: 'Settings',
      offRamp: 'Withdraw to bank',
      onRamp: 'Deposit to wallet',
    },
    signIn: {
      title: 'Welcome',
      description: 'Sign in to your account',
      form: {
        email: 'Email',
        emailPlaceholder: 'Enter your email',
        password: 'Password',
        cta: 'Sign in',
      },
      or: 'or',
      signInWith: 'Sign in with {{provider}}',
      footer: {
        text: 'Don’t have an account?',
        cta: 'Create one',
      },
      error: {
        title: 'Something went wrong',
        text: 'No user was found with that email and password combination. If the problem persists, please contact support.',
      },
      operatingResidency: {
        title: 'Almost there',
        description:
          'We need just a few more details before connecting you in...',
        form: {
          label: 'Operating residency',
          countryPlaceholder: 'Select a country',
        },
        error: {
          title: 'Region not currently supported',
          text: 'Thank you for your interest in using {{merchantName}}. Unfortunately, due to regulatory constraints, we are unable to onboard clients from your country/state at this time. We understand this may be disappointing and apologize for any inconvenience this may cause.<br /><br />If there are any changes to these regulations in the future, we would be happy to reconsider your application. Thank you for your understanding.',
        },
        info: {
          title: 'Please note',
          text: 'A US or EU bank account is required to process transactions, regardless of where you live or operate your business.',
        },
      },
      otp: {
        title: 'Verify your email',
        description: {
          line1: 'We sent a verification link to {{email}}.',
          line2: 'Check your inbox for next steps.',
        },
        cta: 'Continue',
        footer: {
          text: 'Didn’t receive the email?',
          cta: 'Resend it',
        },
        error: {
          invalid: {
            title: 'Invalid code',
            text: 'The code you entered is no longer valid. Please request a new code.',
            buttons: {
              backToSignIn: 'Back to sign in',
              resend: 'Resend',
            },
          },
          mismatch: {
            title: 'Incorrect code',
            text: 'The code you entered is incorrect. Please try again.',
          },
        },
      },
    },
    signUp: {
      title: 'You’ve been invited',
      description:
        'We confirmed your email, now create a password to complete your account setup.',
      form: {
        email: 'Email',
        createAPassword: 'Create a password',
        enterPassword: 'Enter password',
        confirmPassword: 'Confirm password',
        cta: 'Sign up',
      },
    },
    onboarding: {
      title: 'Get started',
      description:
        'Start transferring between USD and crypto in a few simple steps.',
      steps: {
        tos: {
          title: 'Terms of service',
          description:
            'This application uses Sphere and other 3rd parties to securely move funds.',
        },
        connectBank: {
          title: 'Connect bank',
          description: 'Verify your bank manually.',
          tooltipWhenDisabled:
            'Complete the previous steps to connect your bank',
        },
        kyc: {
          title: 'Verify identity',
          description:
            'KYC check is the mandatory process of identifying and verifying identity.',
          tooltipWhenDisabled: 'You must accept the Terms of Service first',
          customerType: {
            individual: {
              title: 'Verify as an individual',
              description:
                'Customer identification times average between 1-30 minutes.',
            },
            business: {
              title: 'Verify a business',
              description:
                'Business verification times average between 3-5 business days.',
            },
            continue: 'Continue',
          },
          alert: {
            description:
              'The next step is verification. Verification can take up to 3 days for cases that require manual review, but is usually faster. After you submit your information we will inform you when you can return to take the next step.',
          },
        },
        msa: {
          title: 'Check your email',
          description:
            'We have sent a DocuSign MSA verification link to {{email}}. Once you sign the document, your status will be updated, and you will be able to proceed to the next step of the onboarding process.',
          cta: 'Sign MSA',
          resendEmailCta: 'Resend Email',
          emailSent: 'Email Sent!',
          pendingSignatureBadge: 'Pending signature',
        },
      },
      badge: {
        pending: 'Pending',
        complete: 'Complete',
        underReview: 'Verifying',
        softRejected: 'Action required',
        rejected: 'Rejected',
        error: 'Error',
        tooltips: {
          underReviewBySumsub:
            'We are currently reviewing your submission via our third-party provider. This usually takes an hour, but sometimes it can take up to 3 days for cases that require manual review. If you experience extended delays beyond this, please contact support.',
          underReviewByBridge:
            'You have been successfully verified by our third-party provider. Please wait while we finalize the process. Check back later or wait until you receive an email regarding your status within 1-3 business days. If you experience extended delays beyond this, please contact support.',
        },
      },
      cta: {
        start: 'Start',
        continue: 'Continue',
        incomplete: 'Start',
        pending: 'In review',
        softRejected: 'Fix issues',
        rejected: 'Contact support',
        error: 'Retry',
        getStarted: 'Get started',
        next: 'Continue',
        checkStatus: 'Check status',
        tooltips: {
          contactSupport:
            "Unfortunately, we couldn't finalize your verification with our internal systems. if you believe this to be a mistake, please contact support for assistance.",
        },
      },
      failedToVerify: {
        title: 'Failed to verify your account',
        description:
          'Thank you for your interest in using RAMP. Unfortunately, due to our current compliance policies, we cannot proceed with onboarding you right now. We understand this may be disappointing and apologize for any inconvenience this may cause. If you believe this decision was a mistake, please contact our team at <0>compliance@spherepay.co</0>.',
        cta: 'Sign out',
        badge: 'Failed',
      },
    },
    myAccounts: {
      title: 'My Accounts',
      bank: {
        tabTitle: 'My Bank Accounts',
        action: 'Connect bank account',
        empty: {
          title: 'No bank accounts',
          description: 'Connect your bank accounts to get started.',
          action: 'Connect bank',
        },
        table: {
          header: {
            bankName: 'Bank',
            bankNameAccountType: {
              checking: 'Checking',
              savings: 'Savings',
              instapay: 'Instapay',
              pesonet: 'Pesonet',
            },
            last4: 'Last 4',
            routingNumber: 'Routing #',
            status: 'Status',
            linkedWallets: 'Linked Wallets',
            actions: 'Actions',
          },
          actions: {
            delete: {
              label: 'Delete',
              dialog: {
                title: 'Delete bank account',
                description:
                  'Are you sure you want to delete this bank account? Any pending transfers involving this bank account will not be processed even if you have already sent funds.',
              },
              cta: 'Delete bank account',
            },
          },
          tooltips: {
            deletedAt: 'You deleted this account {{date}}',
          },
        },
        form: {
          title: 'Add New Bank Account',
          plaidAlert: {
            title: 'Attention',
            description:
              'If you are planning to initiate transfers between cards and bank accounts, <i>or</i> to create automatic on-ramp transfers, your bank <strong>must</strong> be connected via Plaid.',
          },
          plaidButton: 'Connect with Plaid',
          accountOwner: 'Account Owner',
          accountOwnerType: {
            personal: 'Own Account',
            third_party: 'Third-party Business',
          },
          bankType: 'Bank Type',
          currency: 'Currency',
          bankName: 'Bank Name',
          bankNamePlaceholder: 'Start typing...',
          bankNamePlaceholderNoCustomName: 'Select bank...',
          noBankFound: 'Press Enter to select {{name}}',
          accountType: 'Account Type',
          accountHolder: 'Account Holder',
          accountHolderName: 'Account Holder Name',
          accountHolderFirstName: 'First Name',
          accountHolderLastName: 'Last Name',
          accountHolderBirthDate: 'Birth Date',
          business: 'Business',
          businessName: 'Legal Entity Name',
          businessDBAName: 'DBA Name (if applicable)',
          businessType: 'Legal Entity Type',
          businessTaxId: 'EIN/Tax ID number',
          businessWebsite: 'Website',
          businessRegistrationNumber: 'Registration Number',
          businessCardType: {
            soleProprietorship: 'Sole Proprietorship',
            partnership: 'Partnership',
            llc: 'LLC',
            corporation: 'Corporation',
            cooperative: 'Cooperative',
            trust: 'Trust',
          },
          phone: 'Phone number',
          email: 'E-mail',
          routingNumber: 'Routing number',
          accountNumber: 'Account number',
          bic: 'BIC',
          iban: 'IBAN',
          clabe: 'CLABE Number',
          pixKey: 'PIX Key',
          taxID: 'Tax ID',
          channelSubject: 'Channel Subject',
          ifsc: 'IFSC',
          bankID: 'Bank ID',
          bankDocumentNumber: 'Bank Document Number',
          sortCode: 'Sort code',
          branchCode: 'Branch code',
          branchCodeHelpText:
            'Combined format of Institution Number (3 digits) and Transit Number (5 digits).',
          nickname: 'Nickname',
          nicknameDescription: 'Optional.',
          beneficiaryAddress: {
            main: 'Beneficiary address',
            country: 'Country',
            countryPlaceholder: 'Select a country',
            addressLine1: 'Address line 1',
            addressLine2: 'Address line 2',
            city: 'City',
            state: 'State',
            statePlaceholders: {
              default: 'Select a state',
              noCountrySelected: 'Select a country first',
              empty: 'No states found',
            },
            postalCode: 'Postal code',
          },
          cta: 'Add account',
          success: {
            title: 'Bank account added',
            description: 'Your bank account has been added.',
          },
          error: {
            title: 'Something went wrong',
            description:
              'We were unable to add your bank account at this time. ({{error}})',
          },
          verificationPending: {
            title: 'Verification Pending',
            description:
              'We are currently verifying your account. This process may take up to 24 hours. We’ll notify you once the verification is complete.',
            cta: 'Contact Support',
          },
        },
        offloaderWallets: {
          form: {
            networkDropdown: {
              label: 'Select a network:',
              placeholder: 'Select a network:',
            },
            stablecoinDropdown: {
              label: 'Select a token:',
              placeholder: 'Select a token:',
            },
            transferMethodDropdown: {
              label: 'Select a transfer method:',
              placeholder: 'Select a transfer method:',
            },
          },
          table: {
            actions: {
              generateWalletAddress: 'Generate wallet address',
            },
            cell: {
              viewWallets: 'View wallets',
            },
          },
          modal: {
            toast: {
              success: 'Offloader wallet created successfully',
              error: 'Something went wrong with offloader wallet creation',
            },
            title: 'Generate wallet address',
            description:
              'Select the blockchain for generating your new wallet address. This address can be used to receive and send funds with other addresses on the same network.',
            button: {
              confirm: 'Confirm',
              cancel: 'Cancel',
              close: 'Close',
            },
            linkedWallets: {
              title: 'Linked Wallets',
              description:
                'Below are the wallets associated with your bank account. You can use these addresses to receive funds on the respective blockchains.',
              addressHeader: 'Off-Ramp addresses:',
            },
          },
        },
      },
      wallet: {
        tabTitle: 'Wallets',
        actions: {
          primary: 'Connect wallet',
          secondary: 'Enter manually',
        },
        empty: {
          title: 'No wallets',
          description: 'Connect your wallets to get started.',
          action: 'Connect wallet',
        },
        connection: {
          selector: {
            trigger: 'Connect wallet',
            title: 'Connect a wallet on Solana to continue',
            installedDescription: 'Detected',
            moreOptions: 'More options',
          },
        },
        form: {
          title: 'Add {{networkName}} Wallet',
          description: {
            line1:
              'Sending crypto over the wrong network could mean losing your funds.',
            line2:
              'Be sure to choose the right network. When in doubt, send a small amount.',
          },
          nickname: 'Nickname',
          nicknamePlaceholder: 'Eg., Sphere Wallet',
          nicknameDescription: 'Max 20 characters.',
          address: 'Address',
          consent: {
            label: 'I confirm the above information is correct.',
            description:
              'Sending crypto over the wrong network could mean losing your funds.',
          },
          cta: 'Add wallet',
          success: {
            title: 'Wallet added',
            description: 'Your wallet has been added.',
          },
          error: {
            title: 'Something went wrong',
            description:
              'We were unable to add your wallet at this time. ({{error}})',
          },
        },
        editForm: {
          title: 'Edit Wallet',
          cta: 'Save',
          success: {
            title: 'Wallet updated',
            description: 'Your wallet has been updated.',
          },
          error: {
            title: 'Something went wrong',
            description:
              'We were unable to update your wallet at this time. ({{error}})',
          },
          deleteCta: 'Delete',
          deleteDialog: {
            title: 'Delete wallet',
            description: 'Are you sure you want to delete this wallet?',
          },
          deleteSuccess: {
            title: 'Wallet deleted',
            description: 'Your wallet has been deleted.',
          },
          deleteError: {
            title: 'Something went wrong',
            description:
              'We were unable to delete your wallet at this time. ({{error}})',
          },
        },
        items: {
          actions: {
            edit: 'Edit',
            copyAddress: 'Copy address',
            copy: 'Copy',
          },
        },
      },
      card: {
        tabTitle: 'Cards',
        action: 'Connect card',
        empty: {
          title: 'No cards',
          description: 'Connect your cards to get started.',
          action: 'Connect card',
        },
      },
      thirdParty: {
        tabTitle: 'Third-party accounts',
        action: 'Connect 3rd party account',
        table: {
          header: {
            currency: 'Currency',
          },
          actions: {
            delete: {
              label: 'Delete',
              dialog: {
                title: 'Delete bank account',
                description:
                  'Are you sure you want to delete this bank account? Any pending transfers involving this bank account will not be processed even if you have already sent funds.',
              },
              cta: 'Delete 3rd party bank account',
            },
            details: {
              label: 'View details',
              dialog: {
                title: 'Third-party bank account details',
                description:
                  'View the details of the third-party bank account.',
              },
              cta: 'View details',
            },
          },
        },
        empty: {
          title: 'No third-party accounts',
          description: 'Connect your third-party accounts to get started.',
          action: 'Connect 3rd party account',
        },
      },
    },
    createPayout: {
      title: 'Create Payout',
      description: 'Select your transfer method below.',
      onRamp: {
        card: {
          title: 'On-ramp to a digital wallet',
          description: 'On-ramp cash and receive digital currencies.',
        },
        title: 'On-ramp',
        currencies: {
          title: 'Select a currency',
          placeholder: 'Search...',
          options: {
            usd: {
              label: 'USD',
              description: 'United States Dollar',
            },
            eur: {
              label: 'EUR',
              description: 'European Union Euro',
            },
            mxn: {
              label: 'MXN',
              description: 'Mexican Peso',
            },
            brl: {
              label: 'BRL',
              description: 'Brazilian Real',
            },
            php: {
              label: 'PHP',
              description: 'Philippine Peso',
            },
            inr: {
              label: 'INR',
              description: 'Indian Rupee',
            },
          },
          verificationPending: {
            title: 'Verification Pending',
            description:
              'We are currently verifying your account. So you do not have access to any of our supported currencies for now.This process may take up to 24 hours. We’ll notify you once the verification is complete.',
            cta: 'Contact Support',
          },
        },
        methods: {
          title: 'Select a deposit method',
          options: {
            usd: {
              ach: {
                title: 'ACH',
                subtitle: 'Transfer to US bank account',
                description:
                  'Most suitable for everyday payments or recurring transfers within the U.S.',
                badges: {
                  recommended: 'Recommended',
                  domestic: 'Domestic',
                  businessDays01: '0-1 Business Days',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'WIRE',
                subtitle: 'Transfer to US bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Business Days',
                  domestic: 'Domestic',
                  businessDays01: '0-1 Business Days',
                },
              },
              swift: {
                title: 'Swift',
                subtitle: 'Transfer to US bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  comingSoon: 'Coming Soon',
                  international: 'International',
                  businessDays05: '0-5 Business Days',
                },
              },
            },
            eur: {
              sepa: {
                title: 'SEPA',
                subtitle: 'transfer from EU bank account',
                description:
                  'Off-ramp digital currencies and receive EUR to a bank account.',
                badges: {
                  recommended: 'Recommended',
                  instant: 'Instant',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'WIRE',
                subtitle: 'Transfer to EU bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Business Days',
                  domestic: 'Domestic',
                  businessDays01: '0-1 Business Days',
                },
              },
              swift: {
                title: 'Swift',
                subtitle: 'Transfer to US bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  comingSoon: 'Coming Soon',
                  international: 'International',
                  businessDays05: '0-5 Business Days',
                },
              },
            },
            mxn: {
              clabe: {
                title: 'CLABE',
                subtitle: 'Transfer from MX bank account',
                description:
                  'Off-ramp digital currencies and receive MXN to a bank account.',
                badges: {
                  recommended: 'Recommended',
                  instant: 'Instant',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transfer to MX bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Days',
                },
              },
            },
            brl: {
              pix: {
                title: 'PIX',
                subtitle: 'Transfer from BR bank account',
                description:
                  'Off-ramp digital currencies and receive BRL to a bank account.',
                badges: {
                  recommended: 'Recommended',
                  instant: 'Instant',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transfer to BR bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Days',
                },
              },
            },
            php: {
              instapay: {
                title: 'Instapay',
                subtitle: 'Transfer from PH bank account',
                description:
                  'Off-ramp digital currencies and receive PHP to a bank account.',
                badges: {
                  amountsSmallerThan500000: 'Amounts < 500,000 PHP',
                },
              },
              swiftpay_pesonet: {
                title: 'Swiftpay PesoNet',
                subtitle: 'Transfer to PH bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  amountsBiggerOrEqualThan500000: 'Amounts ≥ 500,000 PHP',
                },
              },
            },
            inr: {
              neft: {
                title: 'NEFT',
                subtitle: 'Transfer from IN bank account',
                description:
                  'Off-ramp digital currencies and receive INR to a bank account.',
                badges: {
                  recommended: 'Recommended',
                  instant: 'Instant',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transfer to INR bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Days',
                },
              },
            },
          },
        },
        manualTransferAlert: {
          title: 'On-ramping through Sphere is manual',
          description:
            'To add funds, you’ll need to transfer money from your bank account to Sphere using the details provided in the next step. We’ll notify you via email once the transfer is complete.',
        },
        blocked: {
          title: 'On-ramping is blocked',
          description:
            'You are currently not able to on-ramp any currencies. Please contact support if you need assistance.',
          cta: 'Contact Support',
        },
      },
      pushToCard: {
        card: {
          title: 'On-ramp to a debit card',
          description: 'Send cash from your bank to your debit card.',
        },
      },
      offRamp: {
        title: 'Off-ramp',
        card: {
          title: 'Off-ramp to a bank account',
          description: 'Off-ramp digital currencies to a bank account.',
        },
        currencies: {
          title: 'Select a currency',
          placeholder: 'Search...',
          options: {
            usd: {
              label: 'USD',
              description: 'United States Dollar',
            },
            eur: {
              label: 'EUR',
              description: 'European Union Euro',
            },
            mxn: {
              label: 'MXN',
              description: 'Mexican Peso',
            },
            brl: {
              label: 'BRL',
              description: 'Brazilian Real',
            },
            php: {
              label: 'PHP',
              description: 'Philippine Peso',
            },
            inr: {
              label: 'INR',
              description: 'Indian Rupee',
            },
            gbp: {
              label: 'GBP',
              description: 'British Pound',
            },
            cad: {
              label: 'CAD',
              description: 'Canadian Dollar',
            },
            cop: {
              label: 'COP',
              description: 'Colombian Peso',
            },
            thb: {
              label: 'THB',
              description: 'Thai Baht',
            },
            vnd: {
              label: 'VND',
              description: 'Vietnamese Dong',
            },
            sgd: {
              label: 'SGD',
              description: 'Singapore Dollar',
            },
            idr: {
              label: 'IDR',
              description: 'Indonesian Rupiah',
            },
          },
          verificationPending: {
            title: 'Verification Pending',
            description:
              'We are currently verifying your account. So you do not have access to any of our supported currencies for now.This process may take up to 24 hours. We’ll notify you once the verification is complete.',
            cta: 'Contact Support',
          },
        },
        methods: {
          title: 'Select a withdrawal method',
          options: {
            usd: {
              ach: {
                title: 'ACH',
                subtitle: 'Transfer to US bank account',
                description:
                  'Most suitable for everyday payments or recurring transfers within the U.S.',
                badges: {
                  recommended: 'Recommended',
                  domestic: 'Domestic',
                  businessDays01: '0-1 Business Days',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'WIRE',
                subtitle: 'Transfer to US bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Business Days',
                  domestic: 'Domestic',
                  businessDays01: '0-1 Business Days',
                },
              },
              swift: {
                title: 'Swift',
                subtitle: 'Transfer to US bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  comingSoon: 'Coming Soon',
                  international: 'International',
                  businessDays05: '0-5 Business Days',
                },
              },
            },
            eur: {
              sepa: {
                title: 'SEPA',
                subtitle: 'transfer from EU bank account',
                description:
                  'Off-ramp digital currencies and receive EUR to a bank account.',
                badges: {
                  recommended: 'Recommended',
                  instant: 'Instant',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'WIRE',
                subtitle: 'Transfer to EU bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Business Days',
                  domestic: 'Domestic',
                  businessDays01: '0-1 Business Days',
                },
              },
              swift: {
                title: 'Swift',
                subtitle: 'Transfer to US bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  comingSoon: 'Coming Soon',
                  international: 'International',
                  businessDays05: '0-5 Business Days',
                },
              },
            },
            mxn: {
              spei: {
                title: 'SPEI',
                subtitle: 'Transfer from MX bank account',
                description:
                  'Off-ramp digital currencies and receive MXN to a bank account.',
                badges: {
                  recommended: 'Recommended',
                  instant: 'Instant',
                  noFees: '0 Fees',
                },
              },
            },
            brl: {
              pix: {
                title: 'PIX',
                subtitle: 'Transfer from BR bank account',
                description:
                  'Off-ramp digital currencies and receive BRL to a bank account.',
                badges: {
                  recommended: 'Recommended',
                  instant: 'Instant',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transfer to BR bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Days',
                },
              },
            },
            php: {
              instapay: {
                title: 'Instapay',
                subtitle: 'Transfer from PH bank account',
                description:
                  'Off-ramp digital currencies and receive PHP to a bank account.',
                badges: {
                  amountsSmallerThan50000: 'Amounts < 50,000 PHP',
                },
              },
              swiftpay_pesonet: {
                title: 'Swiftpay PesoNet',
                subtitle: 'Transfer to PH bank account',
                description:
                  'Off-ramp digital currencies and receive PHP to a bank account.',
                badges: {
                  amountsBiggerOrEqualTo50000: 'Amounts ≥ 50,000 PHP',
                },
              },
            },
            inr: {
              neft: {
                title: 'NEFT',
                subtitle: 'Transfer from IN bank account',
                description:
                  'Off-ramp digital currencies and receive INR to a bank account.',
                badges: {
                  recommended: 'Recommended',
                  instant: 'Instant',
                  noFees: '0 Fees',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transfer to INR bank account',
                description: 'Suitable for large or international transfers.',
                badges: {
                  international: 'International',
                  businessDays02: '0-2 Days',
                },
              },
            },
            gbp: {
              fps: {
                title: 'FPS',
                subtitle: 'Transfer to GBP bank account',
                description:
                  'Off-ramp digital currencies and receive GBP to a bank account.',
              },
            },
            cad: {
              eft: {
                title: 'EFT',
                subtitle: 'Transfer to CAD bank account',
                description:
                  'Off-ramp digital currencies and receive CAD to a bank account.',
              },
            },
            cop: {
              pse: {
                title: 'PSE',
                subtitle: 'Transfer to COP bank account',
                description:
                  'Off-ramp digital currencies and receive COP to a bank account.',
              },
            },
            thb: {
              bank_transfer: {
                title: 'Bank Transfer',
                subtitle: 'Transfer to THB bank account',
                description:
                  'Off-ramp digital currencies and receive THB to a bank account.',
              },
            },
            vnd: {
              bank_transfer: {
                title: 'Bank Transfer',
                subtitle: 'Transfer to VND bank account',
                description:
                  'Off-ramp digital currencies and receive VND to a bank account.',
              },
            },
            sgd: {
              fast: {
                title: 'FAST',
                subtitle: 'Transfer to SGD bank account',
                description:
                  'Off-ramp digital currencies and receive SGD to a bank account.',
              },
            },
            idr: {
              bank_transfer: {
                title: 'Bank Transfer',
                subtitle: 'Transfer to IDR bank account',
                description:
                  'Off-ramp digital currencies and receive IDR to a bank account.',
              },
            },
          },
        },
        blocked: {
          title: 'Off-ramping is blocked',
          description:
            'You are currently not able to off-ramp any currencies. Please contact support if you need assistance.',
          cta: 'Contact Support',
        },
      },
      selectors: {
        wallet: {
          title: 'Select Wallet',
          drawerTitle: 'Wallets',
          network: 'Select network',
          confirm: 'Confirm Wallet',
          walletHistory: 'Wallet History',
          addressInputPlaceholder: 'Paste Wallet Address',
          confirmAddressInputButton: 'Use Address',
          multisigSupportBadge: 'Multisig support',
        },
        card: {
          title: 'Select Card',
          drawerTitle: 'Cards',
        },
        bankAccount: {
          title: 'Select Bank Account',
          drawerTitle: 'Bank Accounts',
          emptyTitle: 'No {{currency}} bank accounts found',
        },
      },
      form: {
        youPay: 'You pay',
        youReceive: 'You receive',
        inputEditingTooltip:
          'We will automatically update this amount once you finish editing.',
        maxTransactionSize: 'Max transaction size',
        amountAdjustments: 'Fee breakdown',
        additionalDetails: {
          title: 'Additional details',
          wireMessage: {
            checkbox: 'Add a wire message',
            textareaLabel: 'Description',
            info: 'This message will be visible to the recipient of the transfer. Max characters: 105',
          },
          ffc: {
            checkbox: 'For Further Credit To (FFC)',
            textareaLabel: 'Wire reference',
            textareaPlaceholder: 'Name, 0000-0000',
          },
        },
        cta: {
          selectAWalletFirst: 'Select a wallet first',
          selectACardFirst: 'Select a card first',
          selectABankAccountFirst: 'Select a bank account first',
          create: 'Create transfer',
          getInstructions: 'Get instructions',
        },
        walletBalance: {
          label: 'Wallet balance:',
          actions: {
            max: 'Max',
            half: 'Half',
          },
        },
        errors: {
          walletNotSupported: {
            title: 'Wallet not supported',
            description:
              'This wallet is not supported for the selected transfer method.',
          },
          verifyCurrencyPair: 'Currency pair not supported on this network',
          verifyAmounts: {
            source: {
              min: 'Source amount is less than the minimum',
              max: 'Source amount is greater than the maximum',
            },
            destination: {
              min: 'Destination amount is less than the minimum',
              max: 'Destination amount is greater than the maximum',
            },
          },
        },
      },

      onRampInstructions: {
        title: '{{transferMethod}} transfer',
        transferMethodAlert: {
          title: '{{transferMethod}} transfer only',
          description:
            'Please do not send funds via {{alternativeTransferMethod1}}, {{alternativeTransferMethod2}} or {{alternativeTransferMethod3}}. An actual {{transferMethod}} transfer must be submitted. Ensure that the details match exactly.',
        },
        consent: {
          title: 'Please confirm',
          description:
            'I have sent the correct amount from my bank account to the provided account via {{transferMethod}} using the details provided above.',
        },
      },
      tooltips: {
        sepa: 'To access SEPA payouts, you must complete the KYC process and provide proof of address. If you completed KYC without submitting proof of address, contact your merchant to proceed.',
        isAboveLimit:
          'Input amount exceeds the maximum transaction size of ${maxSingleTxSizeFormatted}',
        currencies: 'This currency is currently not available for your account',
      },
    },
    myPayouts: {
      title: 'Transfer History',
      table: {
        header: {
          type: 'Type',
          source: 'Source',
          destination: 'Destination',
          amountSent: 'Sent',
          amountReceived: 'Received',
          status: 'Status',
          actions: 'Actions',
          date: 'Date',
        },
      },
      onRamps: {
        tabTitle: 'On-ramp',
        empty: {
          title: 'No on-ramp transfers',
          description:
            'With {{merchantName}} you can seamlessly on and off-ramp to exchanges, wallets, and banks. Convert your digital currency into fiat currency and vice versa.',
          action: 'Create transfer',
        },
      },
      offRamps: {
        tabTitle: 'Off-ramp',
        empty: {
          title: 'No off-ramp transfers',
          description:
            'With {{merchantName}} you can seamlessly on and off-ramp to exchanges, wallets, and banks. Convert your digital currency into fiat currency and vice versa.',
          action: 'Create transfer',
        },
      },
      history: {
        tabTitle: 'History',
        empty: {
          title: 'No transfers',
          description:
            'With {{merchantName}} you can seamlessly on and off-ramp to exchanges, wallets, and banks. Convert your digital currency into fiat currency and vice versa.',
          action: 'Create transfer',
        },
      },
      detail: {
        id: 'ID',
        type: {
          onRamp: 'On-ramp',
          offRamp: 'Off-ramp',
          onChain: 'On-chain',
        },
        fiatNetwork: {
          wire: 'Wire transfer',
          ach: 'ACH transfer',
          sepa: 'SEPA transfer',
        },
        info: {
          created: 'Created',
          paymentMethod: 'Payment method',
          email: 'Email',
        },
        payment: {
          title: 'Payment details',
          amount: 'Amount',
          type: 'Type',
          from: 'From',
          to: 'To',
          summary: 'Summary',
          fees: 'Fees',
        },
        bank: {
          title: 'Bank details',
          name: 'Name',
          accountType: 'Account type',
          bankName: 'Bank name',
          beneficiaryName: 'Beneficiary name',
          bankAddress: 'Bank address',
          beneficiaryAddress: 'Beneficiary address',
          accountNumber: 'Account number',
          routingNumber: 'Routing number',
        },
        instructions: {
          title: 'Transfer instructions',
          amountToSend: 'Amount to send',
          message: {
            ach: 'ACH message',
            wire: 'Wire message',
          },
        },
        cancelCta: 'Cancel transfer',
        cancellationWarning: {
          title: 'Double-check before confirming',
          description:
            'Only confirm if you have not transferred any {{currency}} to the address <0>{{address}}</0> on the {{blockchain}} blockchain. Confirming otherwise may result in a loss of funds.',
          cta: 'Confirm cancellation',
        },
        onRampReminder: {
          title: 'On-ramping through Sphere is manual',
          description:
            "If you haven't already, please <0>send a {{transferMethod}} from your bank account to Sphere using the details below</0>. We'll email you once the transfer is complete.",
        },
      },
    },
    tos: {
      alreadyAcceptedCtaText: 'Go back',
      successCtaText: 'Continue',
    },
    languageSelector: {
      label: 'Change language',
    },
    tables: {
      showing: 'Showing {{from}}-{{to}} of {{total}} results',
      previous: 'Previous',
      next: 'Next',
    },
    badges: {
      bankAccount: {
        pending: {
          text: 'Pending',
          description:
            'The bank account is being verified, this process typically takes a few seconds up to a minute. Pending bank accounts cannot be used for transfers.',
        },
        active: {
          text: 'Active',
          description:
            'The bank account is active and ready to be used for transfers.',
        },
        inactive: {
          text: 'Deleted',
          description: 'The bank account was deleted.',
        },
        invalid: {
          text: 'Invalid',
          description: 'The bank account is invalid.',
        },
      },
      payout: {
        pending: {
          text: 'Pending',
          description: 'The transfer is pending approval.',
        },
        funded: {
          text: 'Funded',
          description: 'The transfer has been funded.',
        },
        refunded: {
          text: 'Refunded',
          description: 'The transfer was refunded.',
        },
        processing: {
          text: 'Processing',
          description: 'The transfer is currently being processed.',
        },
        succeeded: {
          text: 'Succeeded',
          description: 'The transfer was successfully completed.',
        },
        failed: {
          text: 'Failed',
          description: 'The transfer failed.',
        },
        failedPrecondition: {
          text: 'Failed',
          description: 'The transfer failed.',
        },
        cancelled: {
          text: 'Cancelled',
          description: 'The transfer was cancelled.',
        },
      },
      kyc: {
        approved: {
          text: 'Verified',
        },
        rejected: {
          text: 'Rejected',
        },
        incomplete: {
          text: 'Incomplete',
        },
        pending: {
          text: 'Pending',
        },
        additionalReviewRequired: {
          text: 'Additional review required',
        },
      },
      sepa: {
        approved: {
          text: 'Enabled',
        },
        incomplete: {
          text: 'Disabled',
        },
      },
    },
    notifications: {
      otpEmailResent: {
        title: 'Email sent',
        description: 'Check your email for the verification code',
      },
      otpEmailResentError: {
        title: 'Something went wrong',
        description:
          'We were unable to resend the verification code at this time. Please try again later.',
      },
      signInSuccess: {
        title: 'Email verified',
        description: 'You are now connected to Ramp',
      },
      kycDone: {
        title: 'KYC completed',
        description: 'Your application is now verified/under review.',
      },
      bankAccountDeleteSuccess: {
        title: 'Bank account deleted',
        description: 'Your bank account has been deleted.',
      },
      bankAccountDeleteError: {
        title: 'Something went wrong',
        description:
          'We were unable to delete your bank account at this time. ({{error}})',
      },
      payouts: {
        create: {
          success: {
            title: 'Transfer initiated',
            offRampDescription: 'Your {{type}} transfer has been initiated.',
            onRampDescription:
              'Your {{type}} transfer has been initiated. To complete the process, please follow the transfer instructions and make {{source}} transfer from your bank account to the bank account details provided on this screen.',
          },
          error: {
            title: 'Something went wrong',
            description:
              'We were unable to create your {{type}} transfer at this time. ({{error}})',
          },
        },
      },
    },
    settings: {
      title: 'Settings',

      loginEmail: {
        label: 'Login email',
        description:
          'Email associated with your RAMP account. This cannot be modified.',
      },
      kyc: {
        title: 'My KYC/B',
        cta: 'Resubmit',
      },
      sepa: {
        title: 'SEPA transfers',
        description: {
          incomplete:
            'This account was verified prior to the addition of SEPA transfers, which require proof of address to gain access. Please resubmit your KYC/B documents to enable SEPA.',
        },
        cta: 'Resubmit',
      },
      customerId: 'Customer ID',
      form: {
        name: 'Name',
        phone: 'Telephone number',
        cta: 'Save changes',
      },
    },
    translations: {
      amountAdjustmentType: {
        networkFee: 'Network fee',
        sphereConversionFee: 'Application conversion fee',
        sphereFixedFee: 'Application fixed fee',
        merchantConversionFee: 'Merchant conversion fee',
        merchantFixedFee: 'Merchant fixed fee',
        conversionFee: 'Conversion fee',
        fxRate: 'Conversion rate',
        noFxRateMessage: 'Unable to calculate conversion rate',
        sponsoredFee: 'Sponsored amount',
        totalFee: 'Total',
        pay: 'You pay',
        receive: 'You receive',
      },
      fees: 'Fees',
    },
    apiErrors: {
      'application/invalid-id': 'Invalid Application ID.',
      'application/not-found': 'Application not found.',
      'ramp/auth/bad-host': 'Ramp is not configured for this merchant.',
      'ramp/auth/host-access': 'Merchants are not allowed to authenticate.',
      'wallets/duplicate': 'Wallet already exists.',
      'wallets/invalid-address': 'Invalid wallet address.',
      'wallets/not-found': 'Wallet not found.',
      'wallets/unsafe-address': 'Unable to interact with this wallet.',
      'wallets/unsupported-network': 'Unsupported network.',
      unknown: 'Something went wrong.',
    },
    apply: 'Apply',
    continue: 'Continue',
    back: 'Back',
    backToSphere: 'Back to Ramp',
    cancel: 'Cancel',
    comingSoon: 'Coming Soon',
    collapse: 'Collapse',
    expand: 'Expand',
    export: 'Export',
    filter: 'Filter',
    copied: 'Copied!',
    selected: 'Selected',
    poweredBy: 'powered by',
    signOut: 'Sign out',
    deleted: 'Deleted',
    type: 'Type',
    network: 'Network',
    status: 'Status',
    reset: 'Reset',
  },
} as const satisfies LocaleResourceBase;

export type LocaleResource = WidenRecordDeep<typeof en>;

export default en;
